import {match} from 'ts-pattern';
import {z} from 'zod';

import {isNil} from 'ramda';

import {parseDate} from 'shared/web';

import type {GridPdfExportContext} from 'features/eag-grid';
import {PdfExportError} from 'features/pdf-export';

import type {WarehouseGridPdfDefinition} from '../types/WarehouseGridPdfDefinition';

const EMPTY_VALUE = '-';
const textCellSchema = z.object({value: z.string().nullable()});
const labelCellSchema = z.object({value: z.object({label: z.string().nullable()}).nullable()});
const decimalSchema = z.union([
  z.number().finite(),
  z
    .string()
    .trim()
    .min(1)
    .refine((value) => Number.isFinite(Number(value)) && !/^[+-]?0[xbo]/iu.test(value))
    .transform(Number)
    .pipe(z.number().finite()),
]);
const numberCellSchema = z.object({value: decimalSchema.nullable()});
const moneyCellSchema = z.object({
  value: z
    .object({
      amount: decimalSchema.nullable(),
      currency: z.string().regex(/^[A-Z]{3}$/u),
    })
    .nullable(),
});
const isoDateSchema = z.union([
  z.string().date(),
  z.string().datetime({offset: true, local: true}),
]);
const formattedValueSchema = z.string().trim().min(1);

type PdfFormatters = {
  date: (value: Date) => string;
  dateTime: (value: Date) => string;
  number: (value: number) => string | null;
  currency: (amount: number, currency: string) => string | null;
};

/** Project only the agreed grid columns; missing or malformed cells never become empty values. */
export function createWarehouseGridPdfRecords(
  context: GridPdfExportContext,
  definition: WarehouseGridPdfDefinition,
  format: PdfFormatters
) {
  if (context.rows.length === 0) {
    throw new PdfExportError('general.notifications.noItemsSelected');
  }
  const titles = new Map(context.columns.map((column) => [column.key, column.title]));

  return context.rows.map((row) => {
    const field = (column: WarehouseGridPdfDefinition['header'][number]) => {
      try {
        const label = formattedValueSchema.parse(titles.get(column.key));
        const cell = row.cells[column.key];
        const value = match(column.kind)
          .with('text', () => textCellSchema.parse(cell).value)
          .with('label', () => labelCellSchema.parse(cell).value?.label)
          .with('number', () => {
            const number = numberCellSchema.parse(cell).value;
            return isNil(number) ? null : formattedValueSchema.parse(format.number(number));
          })
          .with('money', () => {
            const price = moneyCellSchema.parse(cell).value;
            return isNil(price) || isNil(price.amount)
              ? null
              : formattedValueSchema.parse(format.currency(price.amount, price.currency));
          })
          .with('date', 'dateTime', (kind) => {
            const text = textCellSchema.parse(cell).value;
            if (isNil(text) || text.trim().length === 0) {
              return null;
            }
            const date = parseDate(isoDateSchema.parse(text));
            if (Number.isNaN(date.getTime())) {
              throw new RangeError('Invalid warehouse PDF date');
            }
            return formattedValueSchema.parse(
              kind === 'date' ? format.date(date) : format.dateTime(date)
            );
          })
          .exhaustive();

        return {
          key: column.key,
          label,
          value: isNil(value) || value.trim().length === 0 ? EMPTY_VALUE : value,
          share: column.share ?? 1,
        };
      } catch (cause) {
        throw new PdfExportError(
          'module.reportDocument.labels.dataUnavailable',
          false,
          'entity.integration.labels.pleaseRefresh',
          {cause}
        );
      }
    };
    return {
      id: row.id,
      header: definition.header.map(field),
      groups: definition.groups.map((group) => group.map(field)),
    };
  });
}
